/**
 * Split Bill Utilities - Handles precise calculations for split bill functionality
 * Ensures rounding consistency with backend calculations
 */

/**
 * Rounds currency amount using Malaysian rounding rules (nearest 5 cents)
 * Uses the same logic as backend RoundingUtils.java
 * @param amount - Amount to round
 * @returns Rounded amount to 2 decimal places
 */
export function roundCurrencyForSplitBill(amount: number): number {
  if (isNaN(amount) || amount === null || amount === undefined) {
    return 0.00;
  }
  
  // Malaysian currency rounding to nearest 5 cents
  const roundedNumber = Math.round(amount * 20) / 20;
  
  // Ensure exactly 2 decimal places for currency display
  return parseFloat(roundedNumber.toFixed(2));
}

/**
 * Calculates rounding amount for split bill (difference between rounded and original)
 * @param amount - Original amount
 * @returns Rounding adjustment amount
 */
export function calculateSplitBillRoundingAmount(amount: number): number {
  const nearestCent = roundCurrencyForSplitBill(amount);
  const rounding = nearestCent - amount;
  
  // Return precise rounding amount to 2 decimals
  return +rounding.toFixed(2);
}

/**
 * Safely divides amount by quantity for split bill calculations
 * Uses consistent rounding mode matching backend (HALF_EVEN equivalent)
 * @param totalAmount - Total amount to divide
 * @param totalQuantity - Total quantity
 * @param splitQuantity - Quantity for this split
 * @returns Precisely calculated split amount
 */
export function calculateSplitAmount(totalAmount: number, totalQuantity: number, splitQuantity: number): number {
  if (totalQuantity === 0 || splitQuantity === 0) {
    return 0.00;
  }
  
  // Use high precision calculation then round to currency
  const perUnitAmount = totalAmount / totalQuantity;
  const splitAmount = perUnitAmount * splitQuantity;
  
  return roundCurrencyForSplitBill(splitAmount);
}

/**
 * Calculates net amount for split bill item including all components
 * @param subtotal - Item subtotal
 * @param discount - Item discount
 * @param tax - Item tax
 * @param rounding - Item rounding
 * @returns Net amount for split bill
 */
export function calculateSplitBillNetAmount(
  subtotal: number, 
  discount: number = 0, 
  tax: number = 0, 
  rounding: number = 0
): number {
  const netAmount = subtotal - discount + tax + rounding;
  return roundCurrencyForSplitBill(netAmount);
}

/**
 * Validates if split bill calculations match expected totals
 * @param items - Array of split bill items with amounts
 * @param expectedTotal - Expected total amount
 * @returns Validation result with difference
 */
export function validateSplitBillTotals(items: Array<{amount: number}>, expectedTotal: number): {
  isValid: boolean;
  difference: number;
  tolerance: number;
} {
  const calculatedTotal = items.reduce((sum, item) => sum + item.amount, 0);
  const difference = Math.abs(calculatedTotal - expectedTotal);
  const tolerance = 0.02; // 2 cents tolerance for rounding differences
  
  return {
    isValid: difference <= tolerance,
    difference: +difference.toFixed(2),
    tolerance
  };
}

/**
 * Creates unique composite key for order items in split bill
 * @param orderId - Order ID
 * @param itemId - Order item ID
 * @returns Composite key string
 */
export function createSplitBillItemKey(orderId: number, itemId: number): string {
  return `${orderId}_${itemId}`;
}

/**
 * Calculates remaining items after split bill selection
 * @param originalItems - Original order items
 * @param selectedItems - Selected items for payment
 * @returns Updated original items with adjusted quantities
 */
export function calculateRemainingItems<T extends {
  id: number;
  quantity: number;
  paidQuantity: number;
  toBePaidQuantity?: number;
}>(originalItems: T[], selectedItems: T[]): T[] {
  const selectedItemsMap = new Map<number, number>();
  
  // Count selected quantities by item ID
  selectedItems.forEach(item => {
    const currentCount = selectedItemsMap.get(item.id) || 0;
    selectedItemsMap.set(item.id, currentCount + (item.toBePaidQuantity || 1));
  });
  
  // Update original items with new paid quantities
  return originalItems.map(originalItem => {
    const selectedQuantity = selectedItemsMap.get(originalItem.id) || 0;
    if (selectedQuantity > 0) {
      return {
        ...originalItem,
        paidQuantity: (originalItem.paidQuantity || 0) + selectedQuantity,
        toBePaidQuantity: Math.max(0, originalItem.quantity - (originalItem.paidQuantity || 0) - selectedQuantity)
      };
    }
    return originalItem;
  });
}

/**
 * Distributes item-level components (tax, discount, rounding) proportionally for split bills
 * @param totalAmount - Total amount to distribute
 * @param totalQuantity - Total quantity
 * @param splitQuantity - Quantity being split
 * @returns Proportionally distributed amount
 */
export function distributeSplitBillComponent(
  totalAmount: number, 
  totalQuantity: number, 
  splitQuantity: number
): number {
  if (totalQuantity === 0 || splitQuantity === 0) {
    return 0.00;
  }
  
  const perUnitAmount = totalAmount / totalQuantity;
  const distributedAmount = perUnitAmount * splitQuantity;
  
  return roundCurrencyForSplitBill(distributedAmount);
}